/**
 * happa designtool — application entry point.
 *
 * NOTE (Phase A 移行中):
 * Scene 再構築 / preExplosion / explosion の wiring は @render-agent が
 * 後続で実装する。本ファイルは v1.1 契約刷新（spec §6）に追従するための
 * 最小変更のみを保持し、render-agent が二相レンダリングを実装した時点で
 * 再度整理する。
 *
 * 現状で生きているもの:
 * - DEFAULT_STATE で createStore して各 mount* を呼ぶ wiring
 * - DETONATE → rAF ループ（spec §A.3 / Q3 で progress / isAnimating 維持確定）
 * - computeInvalidation の購読（旧 composite / cells フラグは v1.1 で消滅、
 *   新 layout / sceneShape / sceneVoronoi / cellMotion フラグへの actual
 *   wiring は @render-agent 着手時に追加）
 *
 * UI コントロールのうち、v1.1 仕様で削除されたもの（Overlay PNG、
 * Distribution slider）の DOM 撤去は ui-agent が後続フェーズで実施する。
 * 本ファイルでは mount* 呼び出し側の引数を v1.1 に追従するのみ。
 *
 * 依存方向（変更なし）:
 *   ui ──▶ state ──▶ core
 *     │              ▲
 *     └──▶ render ───┘
 *
 * CSS import 順: reset → arare-tokens → tokens → app（変数定義を後段が
 * 参照する順序）。arare-tokens はパネル用、tokens はステージ用。
 */

import './styles/reset.css';
import './styles/arare-tokens.css';
import './styles/tokens.css';
import './styles/app.css';

import { computeDetonateDurationMs } from './core/animation';
import { exportPng, exportWebM, type WebMExportPhase } from './export';
import { buildScene, computeLayout, fitCanvas, render, shouldContinueAnimation } from './render';
import {
  type AppState,
  computeInvalidation,
  createStore,
  DEFAULT_STATE,
  type Store,
} from './state';
import {
  createSection,
  mountAspectRatio,
  mountColorPicker,
  mountExportButtons,
  mountFontLibrary,
  mountKeyboard,
  mountOverlayImage,
  mountPolygonPicker,
  mountRandomizeColors,
  mountShuffle,
  mountSlider,
  mountStage,
  mountTextarea,
  mountTextHarmony,
  mountTimeline,
  mountToggleRow,
} from './ui';

function mountBrand(root: HTMLElement): void {
  const brand = document.createElement('div');
  brand.className = 'brand';

  const left = document.createElement('div');
  const title = document.createElement('div');
  title.className = 'brand-title';
  // Phase J: タイトル / サブタイトル更新。内部コードネーム "happa" は
  // README / package.json に残しつつ、UI 上のブランディングを刷新。
  title.textContent = 'HAPPA VISUAL MACHINE';
  const sub = document.createElement('div');
  sub.className = 'brand-sub';
  sub.style.marginTop = '3px';
  sub.textContent = 'HAPPA BREWING';
  left.appendChild(title);
  left.appendChild(sub);

  const version = document.createElement('div');
  version.className = 'brand-sub';
  version.textContent = 'v1.1';

  brand.appendChild(left);
  brand.appendChild(version);
  root.appendChild(brand);
}

function main(): void {
  const controlsRoot = document.querySelector<HTMLElement>('.controls');
  const stageRoot = document.querySelector<HTMLElement>('.stage');
  if (!controlsRoot || !stageRoot) {
    throw new Error('happa: .controls / .stage root not found in index.html');
  }

  const store: Store<AppState> = createStore<AppState>(DEFAULT_STATE);

  // ── UI ツリーの構築 ────────────────────────────────────────
  // v1.1: Overlay PNG は Q4 確定で一時 unmount（fileInput ファイル本体は
  // 残置、mountFileInput 呼び出しは削除）。Phase C/D で overlay 機能を
  // 再設計する際に戻す。
  // Phase A 実装範囲: Shape（Vertex Count + Shape Jitter）と Perimeter
  // Text（Tracking + Corner Padding）は stub として値バインドのみ。
  // Explosion / Layout / Fonts / Text / Guides 等は Phase B 以降。
  mountBrand(controlsRoot);
  mountAspectRatio(controlsRoot, store);

  const colorsSection = createSection(controlsRoot, 'Colors');
  // Surface / Inner のプリセットスウォッチ。赤は brand accent（spec §A.2 の
  // Inner default #ff3300）、黒は Surface default、灰は中間グレー、白は純白。
  // プリセット 4 色はユーザ指定（2026-07-10 更新）。
  const COLOR_PRESETS = [
    { hex: '#ff0037', name: '赤 Red' },
    { hex: '#000000', name: '黒 Black' },
    { hex: '#ebe6e1', name: '灰 Gray' },
    { hex: '#ffffff', name: '白 White' },
  ] as const;
  // 4 色すべてに鍵アイコン + プリセットスウォッチを揃える（2026-07-10）。
  // RANDOMIZE COLORS の対象は Surface / Inner / Text の 3 色トリオ。
  // Background はランダマイズ対象外だが、鍵は将来のプリセット読込等の
  // プログラム上書きからの保護として意味を持つ。
  const pickerOptions = { lockable: true, presets: COLOR_PRESETS } as const;
  mountColorPicker(colorsSection, store, 'surfaceColor', pickerOptions);
  mountColorPicker(colorsSection, store, 'innerColor', pickerOptions);
  mountColorPicker(colorsSection, store, 'textColor', pickerOptions);
  // Background picker + Transparent toggle は Phase C 修正ラウンドで追加。
  // Transparent ON 時は mountColorPicker 内部の subscriber が input を
  // disabled にし、store の backgroundColor は保持する。
  mountColorPicker(colorsSection, store, 'backgroundColor', pickerOptions);
  mountToggleRow(colorsSection, store, {
    label: 'Transparent',
    field: 'backgroundTransparent',
  });
  // Text Harmony（textMode 切り替え）は RANDOMIZE の直上に置く —
  // ランダマイズの Text 導出方針を変調するコントロールのため。
  mountTextHarmony(colorsSection, store);
  mountRandomizeColors(colorsSection, store);

  // Overlay Images — Phase F Step 4。PNG を 3 スロット（Surface / Inner /
  // Background）で読み込んで合成に使う。state-agent commit adb0768 で
  // `overlayImage` / `overlayImageDataUrl` が `overlays` 構造に再構成された。
  // 各スロットの役割は spec §6 / AppState.overlays を参照。
  // 配置は COLORS の直下（視覚パラメータの一部として表層に位置づける）。
  mountOverlayImage(controlsRoot, store);

  // Layout — spec §3.1 / §6。wireframe v0.10 L157–174 の構成に従い
  // Edge Band / Bleed / Text Margin の 3 スライダ。範囲・step も wireframe
  // を踏襲（Text Margin は 0..20, step 0.5, 小数 1 桁表示）。
  const layoutSection = createSection(controlsRoot, 'Layout');
  mountSlider(layoutSection, store, {
    label: 'Edge Band (%)',
    field: 'gutterPct',
    min: 5,
    max: 25,
    step: 0.5,
    displayTransform: (v) => v.toFixed(1),
  });
  // Bleed: UI は 0..200 整数（%に近い感覚）、store は 0..2.0 倍率。
  // wireframe v0.10 L164–168 と同じスケール。display は小数 2 桁。
  mountSlider(layoutSection, store, {
    label: 'Bleed (×band)',
    field: 'bleedMult',
    min: 0,
    max: 200,
    step: 5,
    displayTransform: (v) => v.toFixed(2),
    storeTransform: (v) => v / 100,
    rangeTransform: (v) => Math.round(v * 100),
  });
  mountSlider(layoutSection, store, {
    label: 'Text Margin (%)',
    field: 'textMarginPct',
    min: 0,
    max: 20,
    step: 0.5,
    displayTransform: (v) => v.toFixed(1),
  });

  // Shape — spec §3.2 / FR-21 / FR-22
  const shapeSection = createSection(controlsRoot, 'Shape');
  mountPolygonPicker(shapeSection, store);
  mountSlider(shapeSection, store, {
    label: 'Shape Jitter',
    field: 'shapeRandomness',
    min: 0,
    max: 100,
    step: 1,
  });
  // Shape Scale — Phase C 追補（state-agent 8a17708）。多角形の内接半径
  // 比率。30..100 [%]、既定 70。Shape Jitter の下に配置（spec 指示）。
  mountSlider(shapeSection, store, {
    label: 'Shape Scale (%)',
    field: 'shapeScale',
    min: 30,
    max: 100,
    step: 1,
  });

  // Breath — Idle Motion (spec §6 Phase D / Phase H Step 1)。progress === 0 時の
  // silhouette 呼吸アニメ。Phase H Step 1 で Crackle / Rotation を削除し、
  // Pulse 単層に簡素化した。`pulseAmp === 0` で呼吸が完全停止する。
  // 配置は SHAPE と FRAGMENTATION の間（視覚パラメータ → 動きパラメータ →
  // 破片パラメータの流れ）。
  // field は `breath.<key>` の dot 付きパスで、mountSlider 内部で
  // `breath` object を shallow clone して更新する（slider.ts 参照）。
  const breathSection = createSection(controlsRoot, 'Breath ─ Idle Motion');
  mountSlider(breathSection, store, {
    label: '鼓動 Pulse Amp',
    field: 'breath.pulseAmp',
    min: 0,
    max: 100,
    step: 1,
  });
  // Period 系は小数 2 桁表示（spec Phase D 指示）。step 0.1 / range 1.0–10.0。
  mountSlider(breathSection, store, {
    label: '鼓動 Pulse Period',
    field: 'breath.pulsePeriod',
    min: 1.0,
    max: 10.0,
    step: 0.1,
    displayTransform: (v) => v.toFixed(2),
  });

  // Fragmentation — Phase J（F）: Count + Shuffle のみ。Randomness / Granularity
  // の UI は撤去し、render 側は `scene.ts` 内のハードコード値で動く。
  const fragSection = createSection(controlsRoot, 'Fragmentation');
  mountSlider(fragSection, store, {
    label: '破片数 Count',
    field: 'fragmentCount',
    min: 10,
    max: 300,
    step: 1,
  });
  mountShuffle(fragSection, store);

  // Explosion — spec §3.7 / §6 / FR-28〜FR-32。破片の爆発運動パラメータ。
  // Phase A で state/types.ts には追加済だったが UI セクションが欠落していた
  // （Phase C 修正ラウンド 2 で補完）。配置は FRAGMENTATION の下、
  // FONT LIBRARY の上（wireframe v0.10 L190 準拠）。
  // セクション名の `─` は U+2500 BOX DRAWINGS LIGHT HORIZONTAL（wireframe 準拠）。
  const explosionSection = createSection(controlsRoot, 'Explosion ─ Fragment Motion');
  mountSlider(explosionSection, store, {
    label: 'Inner Disp (%)',
    field: 'innerDispPct',
    min: 0,
    max: 30,
    step: 1,
  });
  // Perim Disp は store / UI ともに 1.0..4.0 の float（step 0.1）。
  // wireframe は UI 側 100..400 整数のスケール変換だったが、state は float
  // のため identity で渡し、表示だけ toFixed(2) に整形する。
  mountSlider(explosionSection, store, {
    label: 'Perim Disp (×)',
    field: 'perimMult',
    min: 1.0,
    max: 4.0,
    step: 0.1,
    displayTransform: (v) => v.toFixed(2),
  });
  mountSlider(explosionSection, store, {
    label: 'Speed Variance (%)',
    field: 'speedVariance',
    min: 0,
    max: 100,
    step: 1,
  });

  // Font Library — spec §5.5 / §7。Perimeter Text が参照する Latin /
  // Japanese フォントを選ぶ場所なので、Text の前に置く（wireframe L220）。
  const fontLibSection = createSection(controlsRoot, 'Font Library');
  mountFontLibrary(fontLibSection, store);

  // Perimeter Text — spec §6 / FR-33 / FR-34 / FR-38。Phase C で
  // Enable（toggle）/ 本文 textarea / Font Size（スケール変換）/ Tracking /
  // Corner Padding を一括で組む。
  const textSection = createSection(controlsRoot, 'Perimeter Text');
  mountToggleRow(textSection, store, { label: 'Enable', field: 'textEnabled' });
  mountTextarea(textSection, store, { field: 'text' });
  // Font Size (%): range は 10..240 整数、store は 1.0..24.0 float。
  // 逆方向（store→range）と順方向（range→store）を transform ペアで指定。
  mountSlider(textSection, store, {
    label: 'Font Size (%)',
    field: 'fontSizePct',
    min: 10,
    max: 240,
    step: 1,
    displayTransform: (v) => v.toFixed(1),
    storeTransform: (v) => v / 10,
    rangeTransform: (v) => Math.round(v * 10),
  });
  mountSlider(textSection, store, {
    label: 'Tracking',
    field: 'tracking',
    min: -10,
    max: 100,
    step: 1,
  });
  mountSlider(textSection, store, {
    label: '輪郭追従 Follow',
    field: 'followSilhouette',
    min: 0,
    max: 100,
    step: 1,
  });
  // Phase K2 Step 3: Corner Padding スライダーは撤去（Phase C.1 Step 3 で
  // text path が spline 化されて corners=[] になり、cornerPadding は実質
  // no-op だった。dead code を state / UI / render から一括削除）。

  const timeline = mountTimeline(controlsRoot, store);
  const exportButtons = mountExportButtons(controlsRoot, store);

  const stage = mountStage(stageRoot, store);
  const { ctx, canvas } = stage;

  // ── 再描画スケジューラ ────────────────────────────────────
  // store 更新 → 同一フレーム内の複数トリガを 1 回の rAF に集約する。
  let rafPending = false;
  const scheduleRender = (): void => {
    if (rafPending) return;
    rafPending = true;
    requestAnimationFrame(() => {
      rafPending = false;
      render(ctx, store.get());
    });
  };

  // stage がコンテナ resize を観測 → fit 済み。追加で再描画だけ誘発する。
  // canvas 内部解像度の更新は aspectRatio / layout 変化時のみで、resize では
  // 不要（CSS 上のスケールのみ変わる）。
  stage.onResize(scheduleRender);

  // ── canvas サイズ同期（spec §3.1 / 書き出しサイズ = outer 物理寸法） ──
  // aspectRatio / layout 派生フラグが立つたびに canvas.width/height を
  // computeLayout(state).outerW/H へ同期する。同時に CSS 表示サイズも
  // stage 可視領域に contain-fit させる（render/layout.ts の fitCanvas）。
  // ui/stage.ts 内の fitCanvas も ResizeObserver に連動して動くが、
  // canvas の内部解像度を書き換えた直後は CSS も再計算しないと伸び縮みで
  // 表示が崩れるため、ここで明示的に両方を揃える。
  const syncCanvasSize = (): void => {
    const L = computeLayout(store.get());
    const w = Math.round(L.outerW);
    const h = Math.round(L.outerH);
    if (canvas.width !== w) canvas.width = w;
    if (canvas.height !== h) canvas.height = h;
    const rect = stageRoot.getBoundingClientRect();
    const { cssWidth, cssHeight } = fitCanvas(rect.width, rect.height, [w, h]);
    canvas.style.width = `${cssWidth}px`;
    canvas.style.height = `${cssHeight}px`;
  };

  // ── 派生キャッシュの再計算（spec §6.1 / Q2） ─────────────────
  // `sceneCache` は Layout / Shape / Voronoi / Motion いずれかが変わったら
  // 丸ごと再構築。progress 単独変更では呼ばれない（subscribe 側で分岐済み）。
  // sceneCache の書き戻しでさらに subscribe が発火するが、
  // computeInvalidation は `sceneCache` 自身の差分を再計算トリガにしない
  // ため無限ループにならない。ただし rerender フラグは立たないので、呼び出し
  // 側で scheduleRender を別途誘発する必要がある（下の購読者で実施）。
  const rebuildScene = (): void => {
    syncCanvasSize();
    const L = computeLayout(store.get());
    const scene = buildScene(L, store.get());
    store.set({ sceneCache: scene });
  };

  // ── idle 用 rAF ループ（Phase D Breath Motion） ───────────────
  // `scheduleRender` は store 変更駆動の one-shot で時刻駆動の呼吸アニメを
  // 回せないため、需要駆動の連続 rAF を別建てで持つ。detonate 中の rAF
  // （`tick`）とは排他: shouldContinueAnimation() が progress!==0 で false を
  // 返すので二重ループにならない。全 amp=0 の idle でも false → 自然停止。
  let idleRafId: number | null = null;
  const idleTick = (): void => {
    idleRafId = null;
    const state = store.get();
    if (!shouldContinueAnimation(state)) return;
    render(ctx, state);
    idleRafId = requestAnimationFrame(idleTick);
  };
  const ensureIdleAnimation = (): void => {
    if (idleRafId !== null) return;
    if (!shouldContinueAnimation(store.get())) return;
    idleRafId = requestAnimationFrame(idleTick);
  };

  // ── 変更伝播（spec §6.1 / computeInvalidation） ──────────────
  store.subscribe((next, prev) => {
    const flags = computeInvalidation(next, prev);
    // sceneCache を再構築すべきフラグ群（spec §6.1 / Q2 確定）
    if (flags.layout || flags.sceneShape || flags.sceneVoronoi || flags.cellMotion) {
      rebuildScene();
    }
    if (flags.rerender) scheduleRender();
    // breath amp が 0→>0 になった、または detonate 終了で progress が >0→0 に
    // 戻った瞬間に idle ループを起こす。amp=0 や progress>0 の間は no-op。
    ensureIdleAnimation();
  });

  // ── 起爆 rAF ループ ────────────────────────────────────────
  // Phase H Step 2: プログレスバー手動スクラブ化に伴い、rAF tick は毎フレーム
  // `isAnimating` を確認して false なら即 abort する。これは timeline スライダ
  // の input が `isAnimating: false` をセットした瞬間（＝ユーザーがスクラブ
  // 開始した瞬間）、次 tick が過去の animStart 基準で progress を上書きするの
  // を防ぐため。scheduled rAF を明示的に cancel せずとも、tick 内のガードで
  // 十分に「スクラブが勝つ」挙動になる。
  //
  // Phase J: 爆破尺は `state.detonateDuration`（秒）に state 駆動化された。
  // **進行中のアニメに対しては `animDurationMs` をスナップショットして保持**
  // する（途中で尺を変えられると tick 式が破綻するため）。次回 DETONATE 押下
  // で最新値が再評価される。
  let animId: number | null = null;
  let animStart = 0;
  let animDurationMs = computeDetonateDurationMs(store.get());

  const tick = (now: number): void => {
    // ユーザースクラブで isAnimating=false に落ちた場合、ここで chain を切る。
    if (!store.get().isAnimating) {
      animId = null;
      return;
    }
    const p = Math.min(1, (now - animStart) / animDurationMs);
    store.set({ progress: p });
    if (p < 1) {
      animId = requestAnimationFrame(tick);
    } else {
      animId = null;
      store.set({ isAnimating: false });
    }
  };

  const startDetonation = (): void => {
    if (store.get().isAnimating) return;
    if (animId !== null) cancelAnimationFrame(animId);
    // Phase J: 押下時点の detonateDuration を尺として確定する（進行中に UI で
    // 尺を変えても本回のアニメは一貫した尺で完走する）。
    animDurationMs = computeDetonateDurationMs(store.get());
    // Phase H Step 2: 現在 progress からアニメを再開する。ユーザーがスクラブで
    // 0.5 に置いた状態で DETONATE を押したら 0.5 → 1.0 を animDurationMs
    // × (1 - 0.5) ぶんで進める。animStart を過去時刻に巻いて `(now - animStart)
    // / animDurationMs === currentProgress` が成立するようオフセット。
    const currentProgress = store.get().progress;
    // progress===1 から押された場合は完了しているので先に 0 へ戻して
    // 0 → 1.0 の通常コースに乗せる（そのほうが明快）。
    const startFromZero = currentProgress >= 1;
    animStart = startFromZero
      ? performance.now()
      : performance.now() - currentProgress * animDurationMs;
    if (startFromZero) {
      store.set({ isAnimating: true, progress: 0 });
    } else {
      store.set({ isAnimating: true });
    }
    animId = requestAnimationFrame(tick);
  };

  timeline.onDetonate(startDetonation);
  mountKeyboard(store, { onDetonate: startDetonation });

  // ── Export ボタンの wiring（@export-agent） ─────────────────
  // PNG: 現フレームをそのまま保存。GIF ボタンとの視認差のため、書き出し直前に
  // 最新の render を強制実行（ペンディング中の rAF をバイパスして確実な
  // 最新描画を保証）。
  exportButtons.onExportPng(() => {
    render(ctx, store.get());
    exportPng(canvas);
  });
  // Phase I 再設計: PNG / GIF / WebM の 3 系統を 1 つの busy フラグで統制する。
  // いずれか実行中は他を disabled にし、スクラブ等の意図しない干渉を防ぐ。
  // PNG は同期で完結するため busy 制御に入れない（GIF / WebM のみ対象）。
  let exportBusy = false;
  const beginExport = (): void => {
    exportBusy = true;
    exportButtons.setBusy(true);
  };
  const endExport = (): void => {
    exportBusy = false;
    exportButtons.setBusy(false);
    exportButtons.setProgress(null);
  };

  // Phase I 再設計: WebM 書き出し（MediaRecorder 経由）。ffmpeg ベースの MP4
  // 実装は取り下げ、ブラウザ標準 API のみで動く WebM に差し替え。録画時間は
  // モード別（Detonate=2s / Idle Loop=pulsePeriod s / Full Story=5s）で、
  // 進捗は recording 0..1 → finalizing → done の 3 段で通知する。
  // Phase K2 Step 2 で GIF 書き出しは撤去済み（WebM 単独に集約）。
  exportButtons.onExportWebM(async () => {
    if (exportBusy) return;
    beginExport();
    try {
      await exportWebM(canvas, store.get(), (phase: WebMExportPhase, ratio: number) => {
        switch (phase) {
          case 'recording':
            exportButtons.setProgress(`WebM 録画中... ${Math.round(ratio * 100)}%`);
            break;
          case 'finalizing':
            exportButtons.setProgress('WebM ファイナライズ中...');
            break;
          case 'done':
            exportButtons.setProgress('完了 - ダウンロード開始');
            break;
          case 'error':
            exportButtons.setProgress('エラー: コンソールを確認してください');
            break;
        }
      });
    } catch (err) {
      console.error('[main] WebM export error:', err);
    } finally {
      setTimeout(() => endExport(), 800);
    }
  });

  // ── 初期構築（sceneCache → render） ───────────────────────
  rebuildScene();
  scheduleRender();
  ensureIdleAnimation();
}

main();
